import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getHeadCounselorEmail } from '../utils/headCounselorEmails';
import { getEasternToday } from '../utils/dateUtils';

const Summer2026Context = createContext(null);

// Hardcoded 38 camp days — NEVER fail to show dates
const CAMP_DAYS_2026 = [
  '2026-06-20','2026-06-29','2026-06-30','2026-07-01','2026-07-02','2026-07-03',
  '2026-07-06','2026-07-07','2026-07-08','2026-07-09','2026-07-10',
  '2026-07-13','2026-07-14','2026-07-15','2026-07-16','2026-07-17',
  '2026-07-20','2026-07-21','2026-07-22','2026-07-23','2026-07-24',
  '2026-07-27','2026-07-28','2026-07-29','2026-07-30','2026-07-31',
  '2026-08-03','2026-08-04','2026-08-05','2026-08-06','2026-08-07',
  '2026-08-10','2026-08-11','2026-08-12','2026-08-13','2026-08-14',
  '2026-08-17','2026-08-18','2026-08-19',
];

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const SNAPSHOT_DIRECT = `${API}/summer2026/snapshot`;

// Use the pristine fetch saved before analytics scripts could wrap it
// Falls back to regular fetch if __origFetch isn't available (dev mode)
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

// Persist a Snapshot planned absent/late mark to camp_attendance so the report
// reads the same truth the dashboard shows. Backend skips if a real mark exists.
const persistPlannedMark = (camper, date, sa) => {
  safeFetch(`${API}/summer2026/attendance/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      person_id: String(camper.personId),
      date,
      status: sa,
      first_name: camper.firstName,
      last_name: camper.lastName,
      group_code: camper.bunkCode,
      group_name: camper.bunkName,
      session_type: camper.sessionType,
      snapshot_planned: true,
      only_if_unmarked: true,
    }),
  }).catch(err => console.warn('Persist planned mark (non-blocking):', err));
};

const LS_PREFIX = 'summer2026_';
const lsGet = (key, fallback) => {
  try {
    const v = localStorage.getItem(LS_PREFIX + key);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
};
const lsSet = (key, val) => localStorage.setItem(LS_PREFIX + key, JSON.stringify(val));

// Map Snapshot session string to sessionType slug
// Snapshot returns e.g. "Full Season- 5 Days", "Half Season 1- 5 Days", "Flex 30 Days", "6 Week Season"
function mapSessionType(session) {
  if (!session) return 'full';
  const s = session.toLowerCase();
  if (s.includes('half') && s.includes('1')) return 'half1';
  if (s.includes('half') && s.includes('2')) return 'half2';
  if (s.includes('6 week') || s.includes('6week')) return '6week';
  if (s.includes('flex') && s.includes('20')) return 'flex20';
  if (s.includes('flex') && s.includes('25')) return 'flex25';
  if (s.includes('flex') && s.includes('30')) return 'flex30';
  if (s.includes('flex')) return 'flex30';
  if (s.includes('cit')) return 'cit';
  return 'full';
}

// Map Snapshot camper to internal roster format
function mapSnapshotCamper(camper, groupCode) {
  const parts = (camper.name || '').trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  const groupInfo = getHeadCounselorEmail(groupCode);

  return {
    personId: camper.id,
    cmPersonId: camper.person_id || camper.id,
    firstName,
    lastName,
    bunkCode: groupCode,
    bunkName: groupInfo.name || groupCode,
    division: groupCode.startsWith('B') ? 'Boys' : groupCode.startsWith('G') ? 'Girls' : groupCode.startsWith('N') ? 'Nursery' : 'Other',
    sessionType: mapSessionType(camper.session),
    sessionLabel: camper.session || '',
    snapshotAttendance: camper.attendance || 'unmarked',
    daysAttending: [], // Not used with Snapshot - roster is per-date
  };
}

// One-time migration: clear old attendance data that defaulted to 'present'
const ATTENDANCE_VERSION = 'v2_not_selected';
if (localStorage.getItem(LS_PREFIX + 'attendance_version') !== ATTENDANCE_VERSION) {
  localStorage.removeItem(LS_PREFIX + 'attendance');
  localStorage.setItem(LS_PREFIX + 'attendance_version', ATTENDANCE_VERSION);
}

export function Summer2026Provider({ children }) {
  const [loggedInGroup, setLoggedInGroup] = useState(() => lsGet('logged_in_group', null));
  const [isAdmin, setIsAdmin] = useState(() => lsGet('is_admin', false));
  const [roster, setRoster] = useState(() => lsGet('roster', []));
  const [bunks, setBunks] = useState(() => lsGet('bunks', []));
  const [dates, setDates] = useState(() => lsGet('dates', []));
  const [sessionTypes, setSessionTypes] = useState(() => lsGet('session_types', []));
  const [lastSync, setLastSync] = useState(() => lsGet('last_sync', null));
  const [attendance, setAttendance] = useState(() => lsGet('attendance', {}));
  const [swimLessons, setSwimLessons] = useState(() => lsGet('swim_lessons', []));
  const [swimLastSync, setSwimLastSync] = useState(() => lsGet('swim_last_sync', null));
  const [busAttendance, setBusAttendance] = useState(() => lsGet('bus_attendance', null));
  const [busLastSync, setBusLastSync] = useState(() => lsGet('bus_last_sync', null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Persist state
  useEffect(() => { lsSet('logged_in_group', loggedInGroup); }, [loggedInGroup]);
  useEffect(() => { lsSet('is_admin', isAdmin); }, [isAdmin]);
  useEffect(() => { if (roster.length) lsSet('roster', roster); }, [roster]);
  useEffect(() => { if (bunks.length) lsSet('bunks', bunks); }, [bunks]);
  useEffect(() => { if (dates.length) lsSet('dates', dates); }, [dates]);
  useEffect(() => { if (sessionTypes.length) lsSet('session_types', sessionTypes); }, [sessionTypes]);
  useEffect(() => { lsSet('last_sync', lastSync); }, [lastSync]);
  useEffect(() => { lsSet('attendance', attendance); }, [attendance]);
  useEffect(() => { if (swimLessons.length) lsSet('swim_lessons', swimLessons); }, [swimLessons]);
  useEffect(() => { lsSet('swim_last_sync', swimLastSync); }, [swimLastSync]);
  useEffect(() => { if (busAttendance) lsSet('bus_attendance', busAttendance); }, [busAttendance]);
  useEffect(() => { lsSet('bus_last_sync', busLastSync); }, [busLastSync]);

  // Always auto-select today's date (or nearest camp day) on fresh load
  useEffect(() => {
    const today = getEasternToday();
    // June 20 hard override — live test day
    if (today === '2026-06-20') {
      if (selectedDate !== '2026-06-20') setSelectedDate('2026-06-20');
      return;
    }
    if (dates.length && !selectedDate) {
      if (dates.includes(today)) {
        setSelectedDate(today);
      } else {
        const upcoming = dates.find(d => d >= today);
        setSelectedDate(upcoming || dates[dates.length - 1]);
      }
    }
  }, [dates, selectedDate]);

  // Midnight rollover: tablets left open overnight keep yesterday's selectedDate,
  // so "today" lists (signed-out log, incoming banners, roster) show the prior day.
  // Every minute, if the Eastern day changed and the user was on the old "today"
  // (not deliberately browsing another date), advance to the new day.
  const lastTodayRef = useRef(getEasternToday());
  useEffect(() => {
    const iv = setInterval(() => {
      const now = getEasternToday();
      if (now !== lastTodayRef.current) {
        const prev = lastTodayRef.current;
        lastTodayRef.current = now;
        setSelectedDate(sel => {
          if (!sel || sel === prev) {
            if (dates.includes(now)) return now;
            return dates.find(d => d >= now) || sel;
          }
          return sel;
        });
      }
    }, 60000);
    return () => clearInterval(iv);
  }, [dates]);

  // Login: case-insensitive
  // Normalize bunk code: g2 → G02, b1 → B01, G02 → G02, etc.
  const normalizeBunkCode = (input) => {
    const s = input.trim().toUpperCase();
    const match = s.match(/^([A-Z]+)(\d+)$/);
    if (match) return `${match[1]}${match[2].padStart(2, '0')}`;
    return s;
  };

  const login = useCallback((username, password) => {
    const u = username.toLowerCase().trim();
    const p = password.toLowerCase().trim();
    const uNorm = normalizeBunkCode(username);

    // Reset date to auto-pick today on each login
    setSelectedDate(null);

    // Admin login
    if (u === 'admin' && p === 'camp1993') {
      setIsAdmin(true);
      setLoggedInGroup('admin');
      return { success: true, isAdmin: true };
    }

    // Front desk login
    if (u === 'frontdesk' && p === 'camp1993') {
      setIsAdmin(false);
      setLoggedInGroup('frontdesk');
      return { success: true, isFrontDesk: true };
    }

    // Group login: username = bunk code (case/padding insensitive), password = bunk name
    const bunk = bunks.find(
      b => (b.code.toLowerCase() === u || b.code === uNorm) && b.name.toLowerCase() === p
    );
    if (bunk) {
      setIsAdmin(false);
      setLoggedInGroup(bunk.code);
      return { success: true, isAdmin: false, bunk };
    }

    // Fallback: use hardcoded group lookup when sync hasn't loaded bunks yet
    const groupInfo = getHeadCounselorEmail(uNorm);
    if (groupInfo && groupInfo.name && groupInfo.name.toLowerCase() === p) {
      setIsAdmin(false);
      setLoggedInGroup(uNorm);
      return { success: true, isAdmin: false, bunk: { code: uNorm, name: groupInfo.name } };
    }

    return { success: false };
  }, [bunks]);

  const logout = useCallback(() => {
    setLoggedInGroup(null);
    setIsAdmin(false);
  }, []);

  const isAuthenticated = loggedInGroup !== null;

  // Sync roster from Camper Snapshot (primary) with CampMinder fallback
  const syncRoster = useCallback(async (forDate) => {
    setLoading(true);
    setError(null);

    // Helper: try fetching with a single retry
    const tryFetch = async (url, label) => {
      try {
        const res = await safeFetch(url);
        if (!res.ok) throw new Error(`${label} returned ${res.status}`);
        return await res.json();
      } catch (e1) {
        console.warn(`${label} attempt 1 failed: ${e1.message}, retrying...`);
        await new Promise(r => setTimeout(r, 1500));
        const res2 = await safeFetch(url);
        if (!res2.ok) throw new Error(`${label} retry returned ${res2.status}`);
        return await res2.json();
      }
    };

    // Helper: process roster groups into mapped campers + bunks
    const processRoster = (groups) => {
      const mapped = [];
      const bunkList = [];
      groups.forEach(g => {
        const groupInfo = getHeadCounselorEmail(g.group_code);
        bunkList.push({
          code: g.group_code,
          name: groupInfo.name || g.group_code,
          division: g.group_code.startsWith('B') ? 'Boys' : g.group_code.startsWith('G') ? 'Girls' : g.group_code.startsWith('N') ? 'Nursery' : 'Other',
        });
        (g.campers || []).forEach(c => {
          mapped.push(mapSnapshotCamper(c, g.group_code));
        });
      });
      return { mapped, bunkList };
    };

    // Helper: apply roster data to state + cache
    const applyRoster = (mapped, bunkList, campDays) => {
      setRoster(mapped);
      setBunks(bunkList);
      const types = [...new Set(mapped.map(c => c.sessionType))];
      setSessionTypes(types);
      setLastSync(new Date().toISOString());

      // Auto-apply Snapshot attendance (absent/late/dismissed set by admin in Snapshot)
      const targetDate = forDate || selectedDate || (() => {
        const today = getEasternToday();
        if (campDays.includes(today)) return today;
        return campDays.find(d => d >= today) || campDays[campDays.length - 1] || today;
      })();
      setAttendance(prev => {
        const updated = { ...prev };
        mapped.forEach(camper => {
          const sa = camper.snapshotAttendance;
          if (sa === 'absent' || sa === 'late') {
            const key = `${camper.personId}_${targetDate}`;
            const existing = updated[key];
            if (!existing || existing.status === 'not_selected') {
              updated[key] = { status: sa, snapshotPlanned: true };
              persistPlannedMark(camper, targetDate, sa);
            }
          }
        });
        return updated;
      });

      try {
        localStorage.setItem('cached_roster', JSON.stringify(mapped));
        localStorage.setItem('cached_bunks', JSON.stringify(bunkList));
        localStorage.setItem('cached_session_types', JSON.stringify(types));
      } catch (e) { /* ignore quota errors */ }
      return { success: true, roster: mapped, bunks: bunkList, dates: campDays, sessionTypes: types };
    };

    try {
      // 1. Get camp days via backend proxy (includes shared secret)
      let campDays = CAMP_DAYS_2026;
      try {
        const calData = await tryFetch(`${SNAPSHOT_DIRECT}/calendar`, 'Calendar');
        if (calData.camp_days?.length) {
          campDays = calData.camp_days;
          // Always include special test/event days from our hardcoded list
          CAMP_DAYS_2026.forEach(d => {
            if (!campDays.includes(d)) campDays.push(d);
          });
          campDays.sort();
        }
      } catch (e) {
        console.warn('Calendar fetch failed, using hardcoded days');
      }
      setDates(campDays);

      // Force June 20 (live test day) into dates if missing
      if (!campDays.includes('2026-06-20')) {
        campDays = ['2026-06-20', ...campDays].sort();
        setDates(campDays);
      }

      // 2. Determine target date
      const targetDate = forDate || selectedDate || (() => {
        const today = getEasternToday();
        if (campDays.includes(today)) return today;
        const upcoming = campDays.find(d => d >= today);
        return upcoming || campDays[campDays.length - 1] || today;
      })();

      // 3. Fetch roster via backend proxy (includes shared secret)
      let rosterData = null;
      try {
        rosterData = await tryFetch(`${SNAPSHOT_DIRECT}/roster?date=${targetDate}`, 'Roster');
      } catch (e1) {
        console.warn('Roster fetch failed:', e1.message);
      }

      if (rosterData?.groups?.length > 0) {
        const { mapped, bunkList } = processRoster(rosterData.groups);
        return applyRoster(mapped, bunkList, campDays);
      }

      // 4. Both direct + proxy failed — try CampMinder fallback
      throw new Error('No roster data from Snapshot');
    } catch (err) {
      console.error('Snapshot sync failed, trying CampMinder:', err.message);
      try {
        return await syncFromCampMinder();
      } catch (err2) {
        console.error('CampMinder fallback also failed:', err2.message);

        // LAST RESORT: restore from localStorage cache
        try {
          const cachedRoster = JSON.parse(localStorage.getItem('cached_roster'));
          const cachedBunks = JSON.parse(localStorage.getItem('cached_bunks'));
          const cachedTypes = JSON.parse(localStorage.getItem('cached_session_types'));
          if (cachedRoster?.length > 0) {
            console.warn('Using cached roster from localStorage');
            setRoster(cachedRoster);
            setBunks(cachedBunks || []);
            setSessionTypes(cachedTypes || []);
            setDates(CAMP_DAYS_2026);
            setLastSync('cached');
            setError(null);
            return { success: true, roster: cachedRoster, bunks: cachedBunks, dates: CAMP_DAYS_2026, cached: true };
          }
        } catch (e) { /* ignore */ }

        setError('Could not load roster. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate, dates]); // eslint-disable-line react-hooks/exhaustive-deps

  // CampMinder sync (fallback)
  const syncFromCampMinder = useCallback(async () => {
    const res = await safeFetch(`${API}/summer2026/sync`);
    const data = await res.json();
    if (data.success) {
      setRoster(data.roster);
      setBunks(data.bunks);
      setDates(data.dates);
      setSessionTypes(data.sessionTypes);
      setLastSync(new Date().toISOString());
      return data;
    } else {
      setError('Failed to sync roster');
    }
  }, []);

  // Fetch roster for a specific date from Snapshot (used when date changes)
  const fetchSnapshotRoster = useCallback(async (date) => {
    if (!date) return;
    try {
      // Try direct first, then proxy
      let data = null;
      try {
        const res = await safeFetch(`${SNAPSHOT_DIRECT}/roster?date=${date}`);
        data = await res.json();
      } catch (e) {
        console.warn('Direct date fetch failed, trying proxy:', e.message);
        const res = await safeFetch(`${API}/summer2026/snapshot/roster?date=${date}`);
        data = await res.json();
      }

      if (data?.groups?.length > 0) {
        const mapped = [];
        const bunkList = [];
        data.groups.forEach(g => {
          const groupInfo = getHeadCounselorEmail(g.group_code);
          bunkList.push({
            code: g.group_code,
            name: groupInfo.name || g.group_code,
            division: g.group_code.startsWith('B') ? 'Boys' : g.group_code.startsWith('G') ? 'Girls' : g.group_code.startsWith('N') ? 'Nursery' : 'Other',
          });
          (g.campers || []).forEach(c => {
            mapped.push(mapSnapshotCamper(c, g.group_code));
          });
        });
        setRoster(mapped);
        setBunks(bunkList);

        // Auto-apply planned absences AND latenesses from Snapshot (absent/late only — not present)
        setAttendance(prev => {
          const updated = { ...prev };
          mapped.forEach(camper => {
            const sa = camper.snapshotAttendance;
            if (sa === 'absent' || sa === 'late') {
              const key = `${camper.personId}_${date}`;
              const existing = updated[key];
              if (!existing || existing.status === 'not_selected') {
                updated[key] = { status: sa, snapshotPlanned: true };
                persistPlannedMark(camper, date, sa);
              }
            }
          });
          return updated;
        });

        try {
          localStorage.setItem('cached_roster', JSON.stringify(mapped));
          localStorage.setItem('cached_bunks', JSON.stringify(bunkList));
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      console.error('Snapshot date fetch error:', err);
    }
  }, []);

  // When selectedDate changes, re-fetch roster from Snapshot
  const prevDateRef = useRef(null);
  useEffect(() => {
    if (selectedDate && selectedDate !== prevDateRef.current && isAuthenticated) {
      prevDateRef.current = selectedDate;
      fetchSnapshotRoster(selectedDate);
    }
  }, [selectedDate, isAuthenticated, fetchSnapshotRoster]);

  // Auto-sync on first auth if no roster
  useEffect(() => {
    if (isAuthenticated && roster.length === 0) {
      syncRoster();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync swim lessons from backend
  const syncSwimLessons = useCallback(async () => {
    try {
      // First trigger a sync on backend
      await safeFetch(`${API}/summer2026/swim-sync`);
      // Then fetch all stored lessons
      const res = await safeFetch(`${API}/summer2026/swim-lessons`);
      const data = await res.json();
      if (data.success) {
        setSwimLessons(data.lessons);
        setSwimLastSync(new Date().toISOString());
      }
      return data;
    } catch (err) {
      console.error('Swim sync error:', err);
    }
  }, []);

  // Auto-sync swim lessons after roster sync
  useEffect(() => {
    if (isAuthenticated && lastSync) {
      syncSwimLessons();
    }
  }, [isAuthenticated, lastSync]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get swim lesson for a camper on a date
  const getSwimLesson = useCallback((personId, date) => {
    return swimLessons.find(
      l => String(l.person_id) === String(personId) && l.date === date
    ) || null;
  }, [swimLessons]);

  // Fetch stored bus attendance from backend (fast, no external sync)
  const fetchBusAttendance = useCallback(async (date) => {
    try {
      const params = date ? `?date=${date}` : '';
      const res = await safeFetch(`${API}/summer2026/bus-attendance${params}`);
      const data = await res.json();
      if (data.success) {
        setBusAttendance(data);
        setBusLastSync(new Date().toISOString());
      }
      return data;
    } catch (err) {
      console.error('Bus attendance fetch error:', err);
    }
  }, []);

  // Sync bus attendance from external API (slow, triggers full sync)
  const syncBusAttendance = useCallback(async (date) => {
    try {
      const params = date ? `?date=${date}` : '';
      // Trigger sync first (slow - fetches from all buses)
      await safeFetch(`${API}/summer2026/bus-attendance/sync${params}`);
      // Then fetch stored data
      const res = await safeFetch(`${API}/summer2026/bus-attendance${params}`);
      const data = await res.json();
      if (data.success) {
        setBusAttendance(data);
        setBusLastSync(new Date().toISOString());
      }
      return data;
    } catch (err) {
      console.error('Bus attendance sync error:', err);
    }
  }, []);

  // Auto-load stored bus attendance after roster sync (fast fetch, no external sync)
  useEffect(() => {
    if (isAuthenticated && lastSync) {
      fetchBusAttendance();
    }
  }, [isAuthenticated, lastSync]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get bus status for a camper (always uses today's bus attendance)
  const getBusStatus = useCallback((personId) => {
    if (!busAttendance || !busAttendance.records) return null;

    // Find the camper in the roster
    const camper = roster.find(c => String(c.personId) === String(personId));
    if (!camper) return null;

    const nameKey = `${camper.firstName.trim().toLowerCase()}|${camper.lastName.trim().toLowerCase()}`;

    // Check if this camper has a bus assignment
    const busAssignment = busAttendance.name_to_bus?.[nameKey];
    if (!busAssignment) return null; // Not a bus rider

    // Check attendance records for this camper
    const record = busAttendance.records?.find(r =>
      r.first_name.trim().toLowerCase() === camper.firstName.trim().toLowerCase() &&
      r.last_name.trim().toLowerCase() === camper.lastName.trim().toLowerCase()
    );

    if (record) {
      return {
        status: record.status, // present, absent, unmarked, late
        bus_number: record.bus_number,
        marked_at: record.marked_at,
        original_status: record.original_status,
      };
    }

    // Has bus assignment but no attendance record yet
    return {
      status: 'no_data',
      bus_number: busAssignment.am_bus || busAssignment.bus_number,
      marked_at: null,
      original_status: null,
    };
  }, [busAttendance, roster]);

  // Get campers for a date, optionally filtered by bunk
  // With Snapshot, roster is already per-date, so just filter by bunk
  const getCampersForDate = useCallback((date, bunkCode = null) => {
    return roster.filter(c => {
      if (bunkCode && bunkCode !== 'admin' && c.bunkCode !== bunkCode) return false;
      return true;
    });
  }, [roster]);

  // Get groups (bunks) with camper counts for a date
  // With Snapshot, roster is already per-date
  const getGroupsForDate = useCallback((date) => {
    const groups = {};
    roster.forEach(c => {
      if (!groups[c.bunkCode]) {
        const bunk = bunks.find(b => b.code === c.bunkCode);
        groups[c.bunkCode] = {
          code: c.bunkCode,
          name: bunk?.name || c.bunkName,
          division: bunk?.division || c.division,
          campers: [],
        };
      }
      groups[c.bunkCode].campers.push(c);
    });
    return groups;
  }, [roster, bunks]);

  // Attendance helpers
  const getAttendance = useCallback((personId, date) => {
    const key = `${personId}_${date}`;
    return attendance[key] || { status: 'not_selected', lateTime: null, earlyTime: null };
  }, [attendance]);

  const setAttendanceForCamper = useCallback((personId, date, data) => {
    const key = `${personId}_${date}`;
    setAttendance(prev => {
      const existing = prev[key] || { status: 'not_selected', lateTime: null, earlyTime: null };
      const updated = { ...existing, ...data };
      const newState = { ...prev, [key]: updated };

      // Find camper info for backend save
      const camper = roster.find(c => String(c.personId) === String(personId));
      if (camper) {
        // Save to local MongoDB
        safeFetch(`${API}/summer2026/attendance/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            person_id: String(personId),
            date,
            status: updated.status,
            late_time: updated.lateTime || null,
            early_time: updated.earlyTime || null,
            first_name: camper.firstName,
            last_name: camper.lastName,
            group_code: camper.bunkCode,
            group_name: camper.bunkName,
            session_type: camper.sessionType,
          }),
        }).catch(err => console.error('Attendance save error:', err));

        // Sync to Camper Snapshot (present/absent/late)
        const snapshotStatus = ['present'].includes(updated.status) ? 'present'
          : ['late'].includes(updated.status) ? 'late'
          : ['absent'].includes(updated.status) ? 'absent' : null;
        if (snapshotStatus) {
          const markPayload = JSON.stringify({
            camper_id: String(personId),
            status: snapshotStatus,
            date,
          });
          const markOpts = { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: markPayload };
          safeFetch(`${API}/summer2026/snapshot/mark`, markOpts)
            .catch(err => console.error('Snapshot mark error:', err));
        }
      }

      return newState;
    });
  }, [roster]);

  // Load attendance from backend for a date
  const loadAttendanceFromBackend = useCallback(async (date) => {
    try {
      const res = await safeFetch(`${API}/summer2026/attendance/load?date=${date}`);
      const data = await res.json();
      if (data.success) {
        setAttendance(prev => {
          const updated = { ...prev };
          // First, clear any existing entries for this date
          Object.keys(updated).forEach(key => {
            if (key.endsWith(`_${date}`)) {
              delete updated[key];
            }
          });
          // Then load fresh records from backend
          data.records.forEach(rec => {
            const key = `${rec.person_id}_${rec.date}`;
            updated[key] = {
              status: rec.status || 'not_selected',
              lateTime: rec.late_time || null,
              earlyTime: rec.early_time || null,
            };
          });
          return updated;
        });
      }
      return data;
    } catch (err) {
      console.error('Load attendance error:', err);
    }
  }, []);

  // Clear all local attendance state (used after admin clear)
  const clearLocalAttendance = useCallback((date) => {
    if (!date) {
      // Clear ALL - nuke everything: attendance, bus, pickup times, notifications
      setAttendance({});
      localStorage.removeItem(LS_PREFIX + 'attendance');
      localStorage.removeItem(LS_PREFIX + 'bus_attendance');
      localStorage.removeItem(LS_PREFIX + 'bus_last_sync');
      // Clear ALL pickup times and notification states from localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('pickup_') || key.startsWith('notification_') || key.startsWith('webhook_errors_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } else {
      // Clear just the specific date
      setAttendance(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.endsWith(`_${date}`)) {
            delete updated[key];
          }
        });
        return updated;
      });
      // Clear pickup/notification keys for this date
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes(`_${date}`) && (key.startsWith('pickup_') || key.startsWith('notification_')))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    }
    // Also clear bus attendance context
    setBusAttendance(null);
    setBusLastSync(null);
  }, []);

  // Auto-load attendance from backend after sync
  useEffect(() => {
    if (isAuthenticated && lastSync && selectedDate) {
      loadAttendanceFromBackend(selectedDate);
    }
  }, [isAuthenticated, lastSync, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    isAuthenticated, isAdmin, loggedInGroup,
    login, logout,
    roster, bunks, dates, sessionTypes,
    loading, error, lastSync,
    syncRoster, fetchSnapshotRoster,
    selectedDate, setSelectedDate,
    getCampersForDate, getGroupsForDate,
    getAttendance, setAttendanceForCamper, attendance, loadAttendanceFromBackend, clearLocalAttendance,
    swimLessons, swimLastSync, syncSwimLessons, getSwimLesson,
    busAttendance, busLastSync, syncBusAttendance, fetchBusAttendance, getBusStatus,
  };

  return <Summer2026Context.Provider value={value}>{children}</Summer2026Context.Provider>;
}

export function useSummer2026() {
  const ctx = useContext(Summer2026Context);
  if (!ctx) throw new Error('useSummer2026 must be used within Summer2026Provider');
  return ctx;
}
